// ── 지급/공제 항목 관리 섹션 ──────────────────────────────────────────────────
// PayrollSettings에서 분리 — 항목 CRUD (추가/삭제/활성화·비활성화)

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
const sectionTitle = { fontSize: 14, fontWeight: 800, color: 'var(--text-sub)', marginBottom: 12 };
const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnPrimary = { ...stdBlackBtn };
const btnOutline = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};

export default function PayItemsSection({ companyCode, showToast }) {
  const [payItems, setPayItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', type: 'earning', calc_type: 'fixed', is_taxable: true, tax_free_limit: '0' });
  const [addingItem, setAddingItem] = useState(false);

  // ── 항목 목록 로드 ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(async () => {
    const res = await apiClient.get(`/api/admin/payroll/pay-items?company_code=${companyCode}&include_inactive=true`);
    if (res.ok) setPayItems(res.data?.data || []);
  }, [companyCode]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── 항목 추가 ──────────────────────────────────────────────────────────────
  const addPayItem = async () => {
    if (!newItem.name.trim()) { showToast('항목명을 입력하세요.', 'info'); return; }
    setAddingItem(true);
    const res = await apiClient.post('/api/admin/payroll/pay-items', {
      company_code: companyCode, ...newItem, tax_free_limit: parse(newItem.tax_free_limit),
    });
    setAddingItem(false);
    if (res.ok) {
      showToast('항목이 추가되었습니다.', 'success');
      setNewItem({ name: '', type: 'earning', calc_type: 'fixed', is_taxable: true, tax_free_limit: '0' });
      fetchItems();
    } else {
      showToast(res.data?.message || '추가 실패', 'error');
    }
  };

  // ── 항목 삭제 ──────────────────────────────────────────────────────────────
  const deletePayItem = async (id) => {
    const res = await apiClient.delete(`/api/admin/payroll/pay-items/${id}`);
    if (res.ok) { showToast('항목이 삭제되었습니다.', 'success'); fetchItems(); }
    else showToast(res.data?.message || '삭제 실패', 'error');
  };

  // ── 항목 활성/비활성 토글 ──────────────────────────────────────────────────
  const toggleItemActive = async (item) => {
    const res = await apiClient.put(`/api/admin/payroll/pay-items/${item.id}`, { is_active: !item.is_active });
    if (res.ok) fetchItems();
    else showToast(res.data?.message || '변경 실패', 'error');
  };

  // ── 항목 렌더 헬퍼 ─────────────────────────────────────────────────────────
  const renderItem = (item) => (
    <div key={item.id} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
      background: item.is_active ? 'var(--bg-subtle)' : 'transparent', borderRadius: 6,
      opacity: item.is_active ? 1 : 0.5, border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', flex: 1 }}>
        {item.name}
        {item.is_default && <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 4 }}>(기본)</span>}
      </span>
      {item.tax_free_limit > 0 && (
        <span style={{ fontSize: 10, color: '#d97706' }}>비과세 {fmt(item.tax_free_limit)}원</span>
      )}
      {!item.is_default && (
        <>
          <button style={{ ...btnOutline, padding: '2px 8px', fontSize: 10 }} onClick={() => toggleItemActive(item)}>
            {item.is_active ? '비활성화' : '활성화'}
          </button>
          <button style={{ ...btnOutline, padding: '2px 8px', fontSize: 10, color: '#dc2626' }} onClick={() => deletePayItem(item.id)}>
            삭제
          </button>
        </>
      )}
    </div>
  );

  return (
    <div style={cardStyle}>
      <div style={sectionTitle}>지급 / 공제 항목</div>
      {/* 지급 항목 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 6 }}>지급 항목</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {payItems.filter((i) => i.type === 'earning').map(renderItem)}
        </div>
      </div>
      {/* 공제 항목 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>공제 항목</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {payItems.filter((i) => i.type === 'deduction').map(renderItem)}
        </div>
      </div>
      {/* 항목 추가 폼 */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap',
        padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px dashed var(--border)',
      }}>
        <div>
          <label style={labelStyle}>항목명</label>
          <input style={{ ...inputStyle, width: 120 }} value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="예: 직책수당" />
        </div>
        <div>
          <label style={labelStyle}>유형</label>
          <select style={{ ...inputStyle, width: 80 }} value={newItem.type} onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}>
            <option value="earning">지급</option>
            <option value="deduction">공제</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>계산 방식</label>
          <select style={{ ...inputStyle, width: 80 }} value={newItem.calc_type} onChange={(e) => setNewItem({ ...newItem, calc_type: e.target.value })}>
            <option value="fixed">고정</option>
            <option value="manual">수동</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>비과세 한도</label>
          <input style={{ ...inputStyle, width: 90 }} value={newItem.tax_free_limit} onChange={(e) => setNewItem({ ...newItem, tax_free_limit: e.target.value })} placeholder="0" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-sub)', cursor: 'pointer' }}>
          <input type="checkbox" checked={newItem.is_taxable} onChange={(e) => setNewItem({ ...newItem, is_taxable: e.target.checked })} />
          과세
        </label>
        <button style={{ ...btnPrimary, padding: '6px 14px' }} onClick={addPayItem} disabled={addingItem}>
          {addingItem ? '추가 중...' : '+ 추가'}
        </button>
      </div>
    </div>
  );
}
